import { HttpRequest } from "@angular/common/http";
import { inject, Injector } from "@angular/core";
import { JwtConfig, JwtHelperService } from "@auth0/angular-jwt";
import { ShellService } from "@tylertech/incode-module";
import { getLoggerFor, LogAs, LogInvocation, LogLevel, LogReturnAs } from "@tylertech/logging";
import { SecurityRoute, SecurityService, SecurityUrls, ServicePrefix } from "./services";

export class ErpProJwtConfig implements JwtConfig
{
    readonly #injector = inject(Injector);
    readonly #shellService = inject(ShellService);
    #jwtHelperService: JwtHelperService;
    #securityService: SecurityService;

    private readonly log = getLoggerFor("ErpProJwtConfig");

    readonly disallowedRoutes = [
        SecurityUrls.Login,
        SecurityUrls.Refresh,
        SecurityUrls.TylerId,
        SecurityUrls.TylerIdSettings,
    ];

    readonly tokenGetter: (request?: HttpRequest<unknown>) => Promise<string | null> | string | null;

    constructor()
    {
        this.tokenGetter = this.tokenGetterImpl.bind(this);
    }

    /**
     * We have deferred retrieving the SecurityService instance from the injector to avoid runtime cyclic dependency
     * issues.
     */
    private get securityService(): SecurityService
    {
        return this.#securityService
            ?? (this.#securityService = this.#injector.get(SecurityService));
    }

    /**
     * We have deferred retrieving the JwtHelperService instance from the injector to avoid runtime cyclic dependency
     * issues.
     */
    private get jwtHelperService(): JwtHelperService
    {
        return this.#jwtHelperService
            ?? (this.#jwtHelperService = this.#injector.get(JwtHelperService));
    }

    @LogInvocation(LogLevel.DEBUG, true)
    @LogReturnAs((_: string) => `[string Token]`)
    private async tokenGetterImpl(
        @LogAs((x: HttpRequest<unknown>) => `[HttpRequest "${x.url}"]`) request: HttpRequest<unknown>): Promise<string>
    {
        if (request.url.includes(ServicePrefix.NoAuthorizationRequired))
        {
            this.log.debug(`"${ServicePrefix.NoAuthorizationRequired}" url detected; No token required`, { url: request.url });
            return;
        }

        if (!this.securityService.isInitialized)
        {
            throw new Error(`SecurityService has not been initialized yet!`);
        }

        const jwt = this.securityService.Token;
        if (jwt)
        {
            if (!this.#shellService.isEmbedded()
                && this.jwtHelperService.isTokenExpired(jwt, 300))
            {
                return this.securityService.refreshToken()
                    .catch((error: unknown) =>
                    {
                        this.log.error("Error refreshing token", error);

                        this.securityService.clearToken();

                        if (!this.securityService.isRoute([
                            SecurityRoute.Radius,
                            SecurityRoute.Login,
                            SecurityRoute.PasswordReset
                        ]))
                        {
                            this.securityService.logout();
                        }

                        this.log.debug(`requestTokenRefresh | returning null...`);
                        return undefined;
                    });
            }

            return Promise.resolve(jwt);
        }

        if (request.headers.has("Authorization"))
        {
            const token = request.headers.get("Authorization").replace("Bearer ", "");
            this.log.debug("getTokenImpl | using token from Authorization HTTP Header", { token });
            return token;
        }

        if (!this.securityService.isRoute([
            SecurityRoute.Radius,
            SecurityRoute.Login,
            SecurityRoute.Signout,
            SecurityRoute.PasswordReset,
            SecurityRoute.MigrateTid
        ]))
        {
            this.log.warning("No token found");
            // TODO(DO-7967) - should we prompt for login in this case too?
            this.securityService.logout();
        }

        return null;
    }
}
